import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../redux/headerSlice/selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { sliceKey, reducer } from '../redux/headerSlice/slice';
import { headerSaga } from '../redux/headerSlice/saga';
import MiaChatV2ApiService from 'app/NewAIAvatarLayout/services/apiService/miaChat/miaChatApiService';
import { ChatRequest, ChatResponse, Message, Operation, InitRequest, InitResponse, UpdateConversationStateRequest, UpdateConversationStateResponse, Segment } from 'app/NewAIAvatarLayout/services/apiService/miaChat/types';

// Import mock toggle utility for console commands
import 'app/apiService/miaChat/mockToggle';


interface MiaChatV2ContextType {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  
  operations: Operation[];
  setOperations: React.Dispatch<React.SetStateAction<Operation[]>>;
  
  segments: Segment[];
  setSegments: React.Dispatch<React.SetStateAction<Segment[]>>;
  
  currentConversation: ChatResponse | null;
  setCurrentConversation: React.Dispatch<React.SetStateAction<ChatResponse | null>>;
  
  fetchOperations: () => Promise<void>;
  initChat: (selected_operation?: string, signal?: AbortSignal) => Promise<InitResponse | null>;
  startNewChat: (action: string, conversationId: string, signal?: AbortSignal) => Promise<ChatResponse | null>;
  sendChatMessage: (request: ChatRequest, signal?: AbortSignal) => Promise<ChatResponse | null>;
  updateConversationState: (request: UpdateConversationStateRequest, signal?: AbortSignal) => Promise<UpdateConversationStateResponse | null>;
  getUniqueTags: () => string[];
  getOperationsByTag: (tag: string) => Operation[];
  getSegmentsAsOperations: () => Operation[];
}

// ----------- Context Create -----------
const MiaChatV2Context = createContext<MiaChatV2ContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const MiaChatV2Provider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [operations, setOperations] = useState<Operation[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatResponse | null>(null);
  
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  // ----------- API Calls Using MiaChatV2 Service -----------
  const fetchOperations = useCallback(async () => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.getOperations({
        dealer_id: currentDealerId
      });

      if (response.body?.operations) {
        setOperations(response.body.operations);
      }
      
      if (response.body?.segments) {
        setSegments(response.body.segments);
      }
      
      if (!response.body?.operations && !response.body?.segments) {
        setError("No operations or segments found");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch operations");
    } finally {
      setLoading(false);
    }
  }, [currentDealerId]);

  const initChat = useCallback(async (selected_operation?: string, signal?: AbortSignal): Promise<InitResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      const payload: InitRequest = {
        dealer_id: currentDealerId
      };
      
      if (selected_operation) {
        payload.selected_operation = selected_operation;
      }
      
      const response = await MiaChatV2ApiService.initChat(payload, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to initialize chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to initialize chat");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, setError, setLoading]);

  const startNewChat = useCallback(async (selected_option: string, conversationId: string, signal?: AbortSignal): Promise<ChatResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    if (!conversationId) {
      setError("Conversation ID is required");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      // Fallback to API call for other operations
      const response = await MiaChatV2ApiService.startNewChat({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        action: "SELECT_OPERATION",
        selected_option,
        new: true,
        next_action: currentConversation?.next_action || '',
        messages: currentConversation?.messages || []
      }, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to start chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to start chat");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, currentConversation]);

  const sendChatMessage = useCallback(async (request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse | null> => {
    setLoading(true);
    setError("");
    try {
      request.next_action = currentConversation?.next_action;
      // Fallback to API call for unmocked actions
      const response = await MiaChatV2ApiService.sendChatMessage(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to send message");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to send message");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentConversation]);

  const updateConversationState = useCallback(async (request: UpdateConversationStateRequest, signal?: AbortSignal): Promise<UpdateConversationStateResponse | null> => {
    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.updateConversationState(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to update conversation state");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to update conversation state");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ----------- Utility Functions -----------
  
  // Get unique tags from all operations with "All" as default
  const getUniqueTags = useCallback((): string[] => {
    const allTags = operations.flatMap(op => op.tags);
    const uniqueTags = Array.from(new Set(allTags));
    return ['All', ...uniqueTags];
  }, [operations]);

  // Get operations filtered by specific tag
  const getOperationsByTag = useCallback((tag: string): Operation[] => {
    if (tag === 'All') {
      return operations;
    }
    return operations.filter(op => op.tags.includes(tag));
  }, [operations]);

  // Convert segments to operations format for display
  const getSegmentsAsOperations = useCallback((): Operation[] => {
    return segments.map((segment, index) => ({
      id: segment._id,
      action: "SELECT_OPERATION",
      display_text: segment.name,
      description: `Segment: ${segment.name}`,
      icon: "segment",
      tags: ["Segment", "Customer Group"]
    }));
  }, [segments]);

  return (
    <MiaChatV2Context.Provider
      value={{
        loading,
        setLoading,
        error,
        setError,
        operations,
        setOperations,
        segments,
        setSegments,
        currentConversation,
        setCurrentConversation,
        fetchOperations,
        initChat,
        startNewChat,
        sendChatMessage,
        updateConversationState,
        getUniqueTags,
        getOperationsByTag,
        getSegmentsAsOperations,
      }}
    >
      {children}
    </MiaChatV2Context.Provider>
  );
};

// ----------- Custom Hook -----------
export const useMiaChatV2 = (): MiaChatV2ContextType => {
  const context = useContext(MiaChatV2Context);
  if (context === undefined) {
    throw new Error("useMiaChatV2 must be used within a MiaChatV2Provider");
  }
  return context;
};
